/**
 * Behavior for multi-step delete account flow.
 * In order to delete account the user is forced to re-login,
 * and then the flow continues with /account/delete in the URL.
 */
import { useEffect, useState } from 'react'
import { trackEvent } from '@fs/zion-analytics'
import { useUser } from '@fs/zion-user'
import axios from '@fs/zion-axios'
import { clearLastUserAction, getLastUserAction, setLastUserAction } from '../utils/lastUserAction'
import useCounter from './useCounter'
import useErrorReporter from './useErrorReporter'
import withNoticeWarnings from '../services/withNoticeWarnings'
import signOut from '../utils/signOut'
import cisAxiosConfig from '../cisAxiosConfig'

const DialogSteps = {
  confirm: 0,
  finalConfirm: 1,
  deleted: 2,
}

const consideringDelete = 'considering-delete'

function isConsideringDeleteAccount(cisId) {
  const lastUserAction = getLastUserAction(cisId)
  return lastUserAction === consideringDelete
}

function setIsConsideringDeleteAccount(bool, cisId) {
  if (bool) {
    setLastUserAction(consideringDelete, cisId)
  } else {
    clearLastUserAction()
  }
}

const loginAgain = (cisId) => {
  setIsConsideringDeleteAccount(true, cisId)
  window.location.assign('/auth/familysearch/login?prompt=login')
}

const logout = signOut

function useFinallyDeleteAccount(goToNextStep, cisId) {
  const reportError = useErrorReporter()
  const [loading, setLoading] = useState(false)
  const deleteAccount = async () => {
    try {
      setLoading(true)
      // https://sg31b0.familysearch.org/service/ident/cis/cis-public-api/resource_IdentityWebService.html#resource_IdentityWebService_deleteUser_DELETE
      await withNoticeWarnings(axios.delete(`/service/ident/cis/cis-public-api/v4/user/${cisId}`, cisAxiosConfig))
      // TODO: migrate this event to Databricks or Adobe Analytics
      trackEvent({
        event_name: 'account_deleted',
        link_name: 'Account Settings: Account Deleted',
      })
      goToNextStep()
    } catch (error) {
      reportError(error)
    } finally {
      setLoading(false)
    }
  }
  return { deleteAccount, loading }
}

function useHandleConfirmStep({ currentStep, goToNextStep, cisId }) {
  const { deleteAccount, loading } = useFinallyDeleteAccount(goToNextStep, cisId)
  const [redirecting, setRedirecting] = useState(false)

  const handleConfirmStep = async () => {
    if (currentStep === DialogSteps.confirm) {
      setRedirecting(true)
      loginAgain(cisId)
    } else if (currentStep === DialogSteps.finalConfirm) {
      await deleteAccount()
    } else {
      logout()
    }
  }
  return { handleConfirmStep, loading: loading || redirecting }
}

function useHandleCancel({ currentStep, resetSteps, overlay, loading }) {
  const handleCancel = () => {
    if (loading) {
      return
    }
    if (currentStep === DialogSteps.deleted) {
      logout()
      return
    }
    overlay.close()
    resetSteps()
  }
  return handleCancel
}

function useOpenFinalConfirmation({ overlay, setStep, cisId, loading }) {
  useEffect(() => {
    if (!loading && !overlay.isOpen && !overlay.transitioning && isConsideringDeleteAccount(cisId)) {
      setIsConsideringDeleteAccount(false, cisId)
      // Resume delete account flow on page load.
      setStep(DialogSteps.finalConfirm)
      // Not passing a ref here as focusBackRef can be set in useOverlay()
      overlay.open()
    }
  })
}

export default function useDeleteAccount(overlay) {
  const {
    counter: currentStep,
    incrementCounter: goToNextStep,
    resetCounter: resetSteps,
    setCounter: setStep,
  } = useCounter()
  const { cisId } = useUser()
  const { handleConfirmStep, loading } = useHandleConfirmStep({ currentStep, goToNextStep, cisId })

  const handleCancel = useHandleCancel({ currentStep, resetSteps, overlay, loading })

  useOpenFinalConfirmation({ overlay, setStep, cisId, loading })

  return {
    currentStep,
    handleConfirmStep,
    handleCancel,
    loading,
  }
}
